import { ButtonBar, DataGridActionBar, useSnackbar } from "@dain/mui-components";
import {useCallback, useEffect, useRef, useState} from "react";
import { Grid } from "@mui/material";
import { useTranslation } from "react-i18next";
import { useClientLocator } from "../../client/ApiClientLocator";
import PersonGrid from "./PersonGrid";
import { SearchButton } from "../../common/button/CommonButtons";
import { CommonTranslKey } from "../../common/CommonTranslKey";
import { PersonFilterForm } from "./PersonFilterForm";
import {
    PersonFilterDto,
    PersonDto,
} from "../../client/person/PersonApiClient";
import { PageableResultListDto } from "../../client/pagination/PageableResultListDto";
import {SortingOrderType} from "../../client/pagination/SortingFilterDto";
import {PageLoadSize} from "../../common/pagination/PageableGrid";

enum SearchPanelState {
    INITIALIZED,
    IDLE,
    SEARCH_IN_PROGRESS
}

export enum FilterSearchType {
    NEW_SEARCH,
    NEXT_PAGE_SEARCH
}

export interface PersonSearchPanelProps {
    actions?: React.ReactNode;
    onSelectedPerson: (person?: PersonDto) => void;
    onPersonDoubleClick?: (personId: number) => void;
    actionButtonGridReloadCounter: number;
}

export const DEFAULT_PERSON_FILTER: PersonFilterDto = {
    paging: {
        maxPageSize: PageLoadSize.INITIAL_PAGE_SIZE,
        pageToken: null,
    },
    sorting: {
        sortingCriteria: [
            { property: "lastName", order: SortingOrderType.ASC },
            { property: "firstName", order: SortingOrderType.ASC },
        ],
    },
};

export default function PersonSearchPanel(props: Readonly<PersonSearchPanelProps>) {
    const {actions, onSelectedPerson, onPersonDoubleClick, actionButtonGridReloadCounter} = props;

    const {personClient} = useClientLocator();
    const snackbar = useSnackbar();
    const {t} = useTranslation();

    const [lastPersonGridReloadCount, setLastPersonGridReloadCount] = useState<number>(0);

    const [panelStatus, setPanelStatus] = useState<SearchPanelState>(SearchPanelState.INITIALIZED);
    const [personFilter, setPersonFilter] = useState<PersonFilterDto>(DEFAULT_PERSON_FILTER);

    const [personResults, setPersonResults] = useState<PageableResultListDto<PersonDto>>({
        items: [],
        nextPageToken: null,
    });

    const [displayedPersonsCount, setDisplayedPersonsCount] = useState<number>(0);
    const [selectedPerson, setSelectedPerson] = useState<PersonDto>();

    const isInitialized = useRef(false);

    function onFilterChanged(updatedFilter: PersonFilterDto) {
        setPersonFilter(updatedFilter);
    }

    const updatePaginatedPersons = (pageablePerson: PageableResultListDto<PersonDto>, personResults: PageableResultListDto<PersonDto>, searchType: FilterSearchType):PageableResultListDto<PersonDto> => {
        if (searchType === FilterSearchType.NEW_SEARCH) {
            return pageablePerson;
        } else if (searchType === FilterSearchType.NEXT_PAGE_SEARCH) {
            return {
                items: [...personResults.items, ...pageablePerson.items],
                nextPageToken: pageablePerson.nextPageToken || null
            };
        }

        return {
            items: [],
            nextPageToken: null
        };
    };

    const fetchPersons = useCallback((filter: PersonFilterDto, searchType: FilterSearchType) => {
            setPanelStatus(SearchPanelState.SEARCH_IN_PROGRESS);

            personClient.findByFilter(filter)
                .then((filteredPersonsPage) => {
                    const updatedPersons = updatePaginatedPersons(filteredPersonsPage, personResults, searchType);
                    setPersonResults(updatedPersons);
                    setPersonFilter(filter);
                })
                .catch((error) => {
                    snackbar.error(t(CommonTranslKey.UNEXPECTED_ERROR), error);
                })
                .finally(() => {
                    setPanelStatus(SearchPanelState.IDLE);
                });
        },
        [personClient, snackbar, personResults, t]
    );

    const countByFilter = useCallback((filter: PersonFilterDto) => {
            setPanelStatus(SearchPanelState.SEARCH_IN_PROGRESS);
            return personClient
                .countByFilter(filter)
                .then((filteredPersonsCount) => {
                    setDisplayedPersonsCount(filteredPersonsCount);
                })
                .catch((error) => snackbar.error(t(CommonTranslKey.UNEXPECTED_ERROR), error))
                .finally(() => setPanelStatus(SearchPanelState.IDLE));
        },
        [personClient, snackbar, t]
    );

    function resetPagingFilter(personFilter: PersonFilterDto, DEFAULT_PERSON_FILTER: PersonFilterDto) {
        personFilter.paging = DEFAULT_PERSON_FILTER.paging;
    }

    const filterSearch = async () => {
        setPersonResults({ items: [], nextPageToken: null });
        setDisplayedPersonsCount(0);

        resetPagingFilter(personFilter, DEFAULT_PERSON_FILTER);

        fetchPersons(personFilter, FilterSearchType.NEW_SEARCH);
        countByFilter(personFilter);
    };

    const reloadDefaultPersonGrid = useCallback(() => {
        setPersonResults({ items: [], nextPageToken: null });
        setDisplayedPersonsCount(0);

        resetPagingFilter(personFilter, DEFAULT_PERSON_FILTER);

        fetchPersons(personFilter, FilterSearchType.NEW_SEARCH);
        countByFilter(personFilter);
    }, [fetchPersons, countByFilter, personFilter]);

    useEffect(() => {
        if (!isInitialized.current) {
            isInitialized.current = true;
            reloadDefaultPersonGrid();
        }

        if (lastPersonGridReloadCount < actionButtonGridReloadCounter) {
            reloadDefaultPersonGrid();
            setLastPersonGridReloadCount(actionButtonGridReloadCounter);
        }
    }, [reloadDefaultPersonGrid, actionButtonGridReloadCounter, lastPersonGridReloadCount]);

    function onSelectionChange(person: PersonDto) {
        setSelectedPerson(person);
        onSelectedPerson(person);
    }

    return (
        <Grid container spacing={2}>
            <Grid item xs={12}>
                <PersonFilterForm filter={personFilter} onChange={onFilterChanged} />
            </Grid>
            <Grid item xs={12}>
                <ButtonBar>
                    <SearchButton onClick={filterSearch} />
                </ButtonBar>
            </Grid>
            <Grid item xs={12}>
                <Grid container>
                    {actions && (
                        <Grid item xs={12}>
                            <DataGridActionBar actions={actions} />
                        </Grid>
                    )}
                    <Grid item xs={12}>
                        {panelStatus !== SearchPanelState.INITIALIZED && (
                            <PersonGrid
                                rowsData={personResults.items}
                                totalPersonCount={displayedPersonsCount}

                                nextPageToken={personResults.nextPageToken}
                                personFilter={personFilter}
                                fetchPersons={fetchPersons}

                                selectedPersonId={selectedPerson?.id}
                                onSelectPerson={onSelectionChange}
                                onPersonDoubleClick={onPersonDoubleClick}
                            />
                        )}
                    </Grid>
                </Grid>
            </Grid>
        </Grid>
    );
}
